import { Badge, Card, Skeleton, SkeletonGroup } from '@/components/ui';
import { formatCOP } from '@/lib/format';
import type {
  EventPartnerComposition,
  PartnerFlashSale,
  PartnerOffer,
  PartnerProduct,
  PartnerSummary,
} from '@/hooks/useEventFull';

/**
 * PartnerBenefits — sección "Beneficios del partner" del detalle de evento
 * (commerce / taquillera).
 *
 * Compone, debajo de la boletería, los datos del partner de First Class
 * asociados al evento: flash sales, descuentos (offers), merchandising y
 * restaurante. Si el evento suma puntos, muestra el distintivo
 * "Suma puntos · Powered by First Class".
 *
 * Es de PRESENTACIÓN — no toca el carrito ni el checkout (SeatPicker sigue
 * siendo el único punto de compra). Estados: loading / empty digno / render.
 */

type Props = {
  composition: EventPartnerComposition | null | undefined;
  loading?: boolean;
};

function IconSparkles({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className={className}
      aria-hidden
    >
      <path d="M12 3l1.9 4.6L18.5 9.5 13.9 11.4 12 16l-1.9-4.6L5.5 9.5 10.1 7.6 12 3z" />
      <path d="M19 14l.8 2 2 .8-2 .8-.8 2-.8-2-2-.8 2-.8.8-2z" />
    </svg>
  );
}

function IconCrown({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      <path d="M3 7l4 4 5-6 5 6 4-4v11a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V7z" />
    </svg>
  );
}

/** Distintivo "Powered by First Class" — el evento suma puntos. */
export function FcPointsBadge() {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full border border-fc-gold/40 bg-fc-gold/15 px-3 py-1.5 text-xs font-bold text-fc-gold"
      data-testid="fc-points-badge"
    >
      <IconCrown className="h-3.5 w-3.5" />
      <span className="whitespace-nowrap">Suma puntos · Powered by First Class</span>
    </span>
  );
}

function SubSection({
  title,
  count,
  children,
}: {
  title: string;
  count?: number;
  children: React.ReactNode;
}) {
  return (
    <div className="mt-5 first:mt-0">
      <h4 className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-fc-ink/90">
        {title}
        {count != null && <span className="text-xs font-normal text-fc-muted">· {count}</span>}
      </h4>
      {children}
    </div>
  );
}

function PartnerHeader({
  partner,
  grantsFcPoints,
}: {
  partner: PartnerSummary;
  grantsFcPoints: boolean;
}) {
  return (
    <div className="overflow-hidden rounded-card border border-fc-line bg-fc-paper">
      {partner.coverImage && (
        <div className="relative h-28 sm:h-36">
          <img
            src={partner.coverImage}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-fc-paper via-fc-paper/50 to-transparent" />
        </div>
      )}
      <div
        className={`flex flex-wrap items-start gap-4 p-4 ${partner.coverImage ? 'relative -mt-10' : ''}`}
      >
        <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center overflow-hidden rounded-card border border-fc-gold/30 bg-fc-bg">
          {partner.logoUrl ? (
            <img src={partner.logoUrl} alt={partner.name} className="h-full w-full object-cover" />
          ) : (
            <IconSparkles className="h-6 w-6 text-fc-gold" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="truncate font-display text-lg font-bold text-fc-ink">{partner.name}</h3>
            {grantsFcPoints && <FcPointsBadge />}
          </div>
          {partner.benefits && <p className="mt-1 text-sm text-fc-gold/90">{partner.benefits}</p>}
          {partner.description && (
            <p className="mt-1 line-clamp-2 text-xs text-fc-muted">{partner.description}</p>
          )}
          {partner.address && <p className="mt-2 text-[11px] text-fc-muted">{partner.address}</p>}
        </div>
        {partner.discountPercentage != null && partner.discountPercentage > 0 && (
          <Badge tone="success" className="self-center text-sm">
            -{partner.discountPercentage}% socio
          </Badge>
        )}
      </div>
    </div>
  );
}

function FlashSaleCard({ fs }: { fs: PartnerFlashSale }) {
  return (
    <article className="group flex flex-col overflow-hidden rounded-card border border-fc-line bg-fc-paper transition-colors duration-200 hover:border-fc-gold/40">
      {fs.image && (
        <div className="relative h-28 overflow-hidden">
          <img
            src={fs.image}
            alt=""
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
          {fs.discountPercentage != null && fs.discountPercentage > 0 && (
            <span className="absolute right-2 top-2 rounded-full bg-fc-accent px-2 py-0.5 text-[11px] font-bold text-white">
              -{fs.discountPercentage}%
            </span>
          )}
        </div>
      )}
      <div className="flex flex-1 flex-col p-3">
        <h5 className="line-clamp-2 text-sm font-bold text-fc-ink">{fs.title}</h5>
        {fs.description && (
          <p className="mt-1 line-clamp-2 text-[11px] text-fc-muted">{fs.description}</p>
        )}
        <div className="mt-auto flex items-end gap-2 pt-2">
          {fs.finalPriceCents != null && (
            <span className="text-base font-bold text-fc-gold">
              {formatCOP(fs.finalPriceCents)}
            </span>
          )}
          {fs.originalPriceCents != null &&
            fs.finalPriceCents != null &&
            fs.originalPriceCents > fs.finalPriceCents && (
              <span className="text-[11px] text-fc-muted line-through">
                {formatCOP(fs.originalPriceCents)}
              </span>
            )}
        </div>
      </div>
    </article>
  );
}

function OfferCard({ offer }: { offer: PartnerOffer }) {
  const title =
    offer.title || (offer.discountPct ? `Descuento ${offer.discountPct}%` : 'Descuento');
  return (
    <article className="flex items-center gap-3 rounded-card border border-fc-line bg-fc-paper p-3 transition-colors duration-200 hover:border-fc-gold/40">
      <div className="min-w-0 flex-1">
        <h5 className="truncate text-sm font-bold text-fc-ink">{title}</h5>
        {offer.description && (
          <p className="line-clamp-1 text-[11px] text-fc-muted">{offer.description}</p>
        )}
      </div>
      {offer.discountPct != null && offer.discountPct > 0 && (
        <Badge tone="gold">-{offer.discountPct}%</Badge>
      )}
      {offer.finalPriceCents != null && (
        <span className="text-xs font-bold text-fc-gold">{formatCOP(offer.finalPriceCents)}</span>
      )}
    </article>
  );
}

function ProductCard({ product }: { product: PartnerProduct }) {
  const hasFcPrice =
    product.priceFirstClassCents != null &&
    product.pricePublicCents != null &&
    product.priceFirstClassCents < product.pricePublicCents;
  return (
    <article className="group flex flex-col overflow-hidden rounded-card border border-fc-line bg-fc-paper transition-colors duration-200 hover:border-fc-gold/40">
      <div className="relative h-24 overflow-hidden bg-fc-bg">
        {product.image ? (
          <img
            src={product.image}
            alt={product.name}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full items-center justify-center text-fc-line">
            <IconSparkles className="h-7 w-7" />
          </div>
        )}
      </div>
      <div className="flex flex-1 flex-col p-3">
        <h5 className="line-clamp-2 text-xs font-bold text-fc-ink">{product.name}</h5>
        <div className="mt-auto pt-2">
          {hasFcPrice ? (
            <div className="flex items-baseline gap-1.5">
              <span className="text-sm font-bold text-fc-gold">
                {formatCOP(product.priceFirstClassCents)}
              </span>
              <span className="text-[10px] text-fc-muted line-through">
                {formatCOP(product.pricePublicCents)}
              </span>
            </div>
          ) : (
            product.pricePublicCents != null && (
              <span className="text-sm font-bold text-fc-ink">
                {formatCOP(product.pricePublicCents)}
              </span>
            )
          )}
          {hasFcPrice && (
            <span className="mt-1 inline-block rounded-full bg-fc-gold/15 px-1.5 py-0.5 text-[9px] font-bold uppercase text-fc-gold">
              Precio First Class
            </span>
          )}
        </div>
      </div>
    </article>
  );
}

function RestaurantCard({ restaurant }: { restaurant: PartnerSummary }) {
  return (
    <article className="flex flex-wrap items-center gap-4 rounded-card border border-fc-line bg-fc-paper p-4">
      <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded-card border border-teal/30 bg-teal/10">
        {restaurant.logoUrl ? (
          <img
            src={restaurant.logoUrl}
            alt={restaurant.name}
            className="h-full w-full object-cover"
          />
        ) : (
          <IconSparkles className="h-5 w-5 text-teal" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <h5 className="truncate text-sm font-bold text-fc-ink">{restaurant.name}</h5>
        {restaurant.benefits && (
          <p className="line-clamp-1 text-[11px] text-fc-gold/80">{restaurant.benefits}</p>
        )}
        {restaurant.address && (
          <p className="mt-0.5 text-[11px] text-fc-muted">{restaurant.address}</p>
        )}
      </div>
      {restaurant.discountPercentage != null && restaurant.discountPercentage > 0 && (
        <Badge tone="info">-{restaurant.discountPercentage}% First Class</Badge>
      )}
    </article>
  );
}

export function PartnerBenefits({ composition, loading }: Props): JSX.Element | null {
  if (loading) {
    return (
      <section className="mt-10">
        <SkeletonGroup label="Cargando beneficios del partner" className="flex flex-col gap-4">
          <Skeleton shape="h-6 w-56" />
          <Skeleton shape="h-24 w-full" />
          <Skeleton shape="h-32 w-full" />
        </SkeletonGroup>
      </section>
    );
  }

  if (!composition) return null;

  const {
    partner,
    flashSales,
    offers,
    merchandising,
    restaurant,
    grantsFcPoints,
    hasPartnerBenefits,
  } = composition;

  // Sin partner ni beneficios: solo el distintivo si suma puntos; si no, nada.
  if (!hasPartnerBenefits) {
    if (!grantsFcPoints) return null;
    return (
      <section className="mt-10" data-testid="partner-benefits">
        <Card
          variant="outlined"
          padded
          className="flex flex-wrap items-center gap-3 border-fc-gold/30"
        >
          <FcPointsBadge />
          <p className="text-sm text-fc-muted">
            Este evento{' '}
            <span className="font-semibold text-fc-ink">suma puntos de First Class</span> con tu
            compra.
          </p>
        </Card>
      </section>
    );
  }

  return (
    <section
      className="mt-10"
      data-testid="partner-benefits"
      aria-labelledby="partner-benefits-heading"
    >
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2
          id="partner-benefits-heading"
          className="flex items-center gap-2 font-display text-2xl text-fc-gold"
        >
          <IconSparkles className="h-5 w-5" />
          Beneficios del partner
        </h2>
        {grantsFcPoints && <FcPointsBadge />}
      </div>

      <Card variant="ghost" padded>
        {partner && (
          <div className="mb-6">
            <PartnerHeader partner={partner} grantsFcPoints={grantsFcPoints && !!partner} />
          </div>
        )}

        {flashSales.length > 0 && (
          <SubSection title="Flash sales" count={flashSales.length}>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {flashSales.map((fs) => (
                <FlashSaleCard key={fs.id} fs={fs} />
              ))}
            </div>
          </SubSection>
        )}

        {offers.length > 0 && (
          <SubSection title="Descuentos" count={offers.length}>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {offers.map((offer) => (
                <OfferCard key={offer.id} offer={offer} />
              ))}
            </div>
          </SubSection>
        )}

        {merchandising.length > 0 && (
          <SubSection title="Merchandising" count={merchandising.length}>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {merchandising.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </SubSection>
        )}

        {restaurant && (
          <SubSection title="Restaurante asociado">
            <RestaurantCard restaurant={restaurant} />
          </SubSection>
        )}

        {grantsFcPoints && (
          <div className="mt-6 rounded-card border border-fc-gold/20 bg-fc-gold/5 p-3">
            <p className="text-xs text-fc-muted">
              Comprando en este evento{' '}
              <span className="font-semibold text-fc-ink">sumás puntos de First Class</span> que
              podés canjear por beneficios y experiencias.
            </p>
          </div>
        )}
      </Card>
    </section>
  );
}
